/* oxlint-disable no-explicit-any */
import React from "react";

// Inspired by https://github.com/primer/react

// slot config allows 2 options:
// 1. Component to match, example: { leadingVisual: LeadingVisual }
type ComponentMatcher = React.ElementType<UnknownProps>;
// 2. Component to match + a test function, example: { blockDescription: [Description, props => props.variant === 'block'] }
type ComponentAndPropsMatcher = [
  ComponentMatcher,
  (props: UnknownProps, displayName: string | undefined) => boolean,
];

export type SlotConfig = Record<
  string,
  ComponentMatcher | ComponentAndPropsMatcher
>;

// We don't know what the props are yet, we set them later based on slot config
type UnknownProps = any;

export type SlotValue<
  Config,
  Property extends keyof Config,
> = Config[Property] extends React.ElementType // config option 1
  ? React.ReactElement<React.ComponentProps<Config[Property]>, Config[Property]>
  : Config[Property] extends readonly [
        infer ElementType extends React.ElementType, // config option 2, infer array[0] as component
        infer _testFn, // even though we don't use testFn, we need to infer it to support types for slots.*.props
      ]
    ? React.ReactElement<React.ComponentProps<ElementType>, ElementType>
    : never; // useful for narrowing types, third option is not possible

export function isChildOfType(
  child: React.ReactNode,
  type: ComponentMatcher | ComponentAndPropsMatcher
): child is React.ReactElement<
  unknown,
  string | React.JSXElementConstructor<any>
> {
  if (!React.isValidElement(child)) {
    return false;
  }

  if (Array.isArray(type)) {
    const [component, testFn] = type;
    const displayName = (child.type as any).displayName;
    return child.type === component && testFn(child.props, displayName);
  }

  return child.type === type;
}

/** Map the values of an object */
export function buildSlots<Config extends SlotConfig, V>(
  config: Config,
  fn: (value: Config[keyof Config]) => V
) {
  return Object.keys(config).reduce(
    (result, key: keyof Config) => {
      result[key] = fn(config[key]);
      return result;
    },
    {} as Record<keyof Config, V>
  );
}
